import { useDispatch, useSelector } from 'react-redux'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useState, useEffect, useMemo } from 'react'
import { debounce, isEmpty, sortBy } from 'lodash'

import database from 'firebase-config'
import { off, onValue, ref } from 'firebase/database'

import { sleep } from 'utils/functions/helpers'
import { getAllTeams } from '../../../../components/data/dataServices'
import { getDevices, getDevicesByTeam } from '../../../core/services/environmentService'

const mobileUnitsKey = {
	allTeams: 'teams',
	allDevices: 'devices',
}

function useMobileUnitsQuery(props) {
	const { tile, expandAllMobileGroups, selectedTeams, repeatReloadMapSeconds } = props
	const { environment } = useSelector((state) => state)
	const [mobileUserRef, setMobileUserRef] = useState('')
	const [isLoadingFirebase, setIsLoadingFirebase] = useState(false)

	const [data, setData] = useState([])

	const {
		data: teamsData,
		isLoading: isLoadingTeams,
		isFetching: isFetchingTeams,
		refetch: refetchTeamsData,
	} = useQuery(
		['mobileUnits', tile.key, mobileUnitsKey.allTeams],
		() => getAllTeams(environment.apiToken),
		{
			enabled: Boolean(tile.key),
		}
	)

	const { data: devicesData, refetch: refetchDevicesData } = useQuery(
		['mobileUnits', tile.key, mobileUnitsKey.allDevices],
		() => getDevices(environment.apiToken),
		{
			enabled: Boolean(tile.key) && expandAllMobileGroups,
			refetchInterval: (repeatReloadMapSeconds || 10) * 1000,
		}
	)

	useEffect(() => {
		const customerKey = environment.account.key
		if (!customerKey) return

		if (!isEmpty(mobileUserRef)) {
			off(mobileUserRef)
		}

		setMobileUserRef(ref(database, `userSessions/mobileUserUpdate/customer/${customerKey}`))
	}, [environment.account.key])

	const debouncedRefetch = debounce(() => {
		try {
			setIsLoadingFirebase(true)
			refetchDevicesData()
			setIsLoadingFirebase(false)
		} catch (error) {
			console.log(error)

			setIsLoadingFirebase(false)
		}
	}, 500)

	useEffect(() => {
		if (isEmpty(mobileUserRef)) return

		const unSubscribeMobileUnitOffset = onValue(mobileUserRef, (snapshot) => {
			sleep(2000).then(() => {
				debouncedRefetch()
			})
		})

		return () => {
			unSubscribeMobileUnitOffset()
		}
	}, [mobileUserRef])

	const queries = useMemo(() => {
		if (isEmpty(selectedTeams)) return []
		return selectedTeams.map((team) => {
			return {
				queryKey: ['mobileUnits', tile.key, team],
				queryFn: () => {
					return getDevicesByTeam(environment?.apiToken, team)
				},
				enabled: Boolean(tile.key) && !expandAllMobileGroups,
				refetchInterval: (repeatReloadMapSeconds || 10) * 1000,
			}
		})
	}, [selectedTeams, expandAllMobileGroups, tile.key])

	const queriesResult = useQueries({ queries })
	const devicesByTeams = queriesResult
		.map((result) => result.data?.data)
		.flat()
		.filter(Boolean)
	const isLoadingDevicesByTeams = queriesResult.some(
		(result) => result.isLoading || result.isFetching || result.status === 'idle'
	)
	const loadingData = useMemo(() => {
		if (expandAllMobileGroups) {
			return isFetchingTeams || isLoadingTeams
		}
		return isFetchingTeams || isLoadingTeams || isLoadingDevicesByTeams
	}, [isLoadingTeams, isFetchingTeams, isLoadingDevicesByTeams, expandAllMobileGroups])

	const filterDevices = useMemo(() => {
		try {
			if (loadingData) return []
			const devices = devicesData?.data
			if (!Array.isArray(devices) || isEmpty(devices)) return []
			return devices
		} catch (error) {
			console.log('Error filtering devices:', error)
			return []
		}
	}, [loadingData, devicesData?.data])

	useEffect(() => {
		if (expandAllMobileGroups) {
			if (loadingData) return
			let teams = sortBy(teamsData.data, (team) => team.name.toLowerCase())

			const result = teams.map((team) => ({
				teamKey: team.key,
				teamName: team.name,
				devices: filterDevices.filter((device) => device.teamKey === team.key),
			}))

			const noTeamDevices = filterDevices.filter((device) => {
				const deviceTeam = teams.find((team) => device.teamKey === team.key)
				return !deviceTeam
			})
			if (!isEmpty(noTeamDevices)) {
				result.push({
					teamKeu: 'noGroup',
					teamName: 'No Group',
					devices: noTeamDevices,
				})
			}

			setData(result.filter((item) => !isEmpty(item.devices)))
		} else {
			if (loadingData) return
			let teams = sortBy(teamsData.data, (team) => team.name.toLowerCase())
			const result = teams.map((team) => ({
				teamKey: team.key,
				teamName: team.name,
				alreadyLoadingDevices: selectedTeams?.includes(team.key),
				devices: devicesByTeams.filter((device) => device.teamKey === team.key),
			}))

			setData(result)
		}
	}, [loadingData, teamsData, filterDevices, expandAllMobileGroups])

	function refetchData() {
		refetchTeamsData()
		refetchDevicesData()
	}

	return {
		mobileUnitsGroups: teamsData?.data ?? [],
		isLoading: loadingData || isLoadingFirebase,
		data,
		refetchData,
	}
}

export default useMobileUnitsQuery
